import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, map, switchMap } from 'rxjs';
import { environment } from '../../environments/environment';
import { PlanV2IssueQueueService } from './plan-v2-issue-queue.service';
import { buildPlanV2IssueKey, type PlanV2IssueCardPayload } from '../utils/plan-v2-issues';
import { siteWideCodeFromKey } from '../utils/site-wide-suppression';
import {
  reviewCheckWritePlan,
  type ReviewCheckWrite
} from '../utils/review-check-writes';
import { mutedCheckCodes } from '../utils/review-check-memory';
import {
  reviewSendRequest,
  reviewUndoRequest,
  splitReviewSelection,
  type ReviewSelectableItem,
  type ReviewSelectionSplit
} from '../utils/review-selection';

/**
 * What the page under review is about to send.
 *
 * A service rather than inputs and outputs because the two ends are far apart:
 * the findings are rendered by issue cards buried inside a 6,000-line
 * `url-detail`, and the button that sends them lives in `review-mode`'s action
 * bar. Threading a payload up through that would mean `url-detail` growing an
 * output for something it does not own, and `review-mode` reaching into a
 * component it only embeds.
 *
 * **Cards register themselves.** A card knows its own payload; nothing else has
 * to enumerate what findings a page has, which is what keeps this from needing
 * an update every time a new kind of card is added.
 *
 * **Keyed by URL, never cleared on navigation.** Issue keys are unique per URL,
 * so a stale registration cannot leak onto another page — and not clearing
 * means there is no ordering race between a page changing and its cards
 * mounting. A belt is at most a few hundred entries; the memory is not worth a
 * bug.
 */
/** One technical check on a page, as the card that renders it sees it. */
export interface RegisteredCheck {
  /** The DataForSEO check name — the same join key the rollup uses. */
  code: string;
  /** The card it is listed under, so a card knows when it has been emptied. */
  cardIssueKey: string;
  /**
   * Whether the crawler reports this on the template rather than the page.
   *
   * The only thing scope decides is whether SENDING it settles it for the rest
   * of the period. Unticking is scope-blind: a reviewer who does not want page
   * titles shortened does not want them shortened on page 2 either.
   */
  siteWide: boolean;
}

@Injectable({ providedIn: 'root' })
export class ReviewSelectionService {
  private readonly API_URL = environment.apiUrl + '/v1';

  /** Registered findings, per URL, in the order the cards mounted. */
  private readonly itemsByUrlId = new Map<string, Map<string, ReviewSelectableItem>>();

  /** The exceptions — see the note in `utils/review-selection`. */
  private readonly deselected = new Set<string>();

  /**
   * Every technical check on each page, with the card it belongs to and whether
   * the crawler calls it site-wide.
   *
   * This started as a side channel for site-wide codes only — they were not
   * separate decisions, they rode along with the card that reported them. They
   * are separate decisions now: a card is "fix these" and the checks under it
   * are "which of these", and a reviewer who wants six of eight has to be able
   * to say so without unticking the whole card.
   */
  private readonly checksByUrlId = new Map<string, Map<string, RegisteredCheck>>();

  /**
   * The individual rows inside a card — the pages under Supporting pages, the
   * sections under Missing sections — keyed by the card they belong to.
   *
   * Separate from `checksByUrlId`, and deliberately so. A technical check is a
   * POLICY: unticking "Page title too long" says something about every page
   * left in the queue, and it is remembered. A row here is a THING: unticking
   * `/blog/actual-cost-in-project-management/` says you are not writing that
   * page, which tells you nothing about the next page's rows. So these are
   * session-local and never written, and no memory follows them anywhere.
   */
  private readonly cardItemsByUrlId = new Map<string, Map<string, string[]>>();

  /** The exceptions, same way round as the card selection. */
  private readonly deselectedItems = new Set<string>();

  /**
   * Checks unticked ON THIS CARD only, keyed `${cardIssueKey}::${code}`.
   *
   * The difference from `checkTicks` is the whole reason this exists. Unticking
   * a check by clicking it is a statement about the CHECK — "not fixing page
   * titles" — and it is remembered for the period and applies to every page
   * left. Unticking one because the operator picked a different row on this
   * card is a statement about THIS PAGE, and says nothing about the check at
   * all.
   *
   * Without the distinction, clicking one row to reactivate a card would have
   * muted every other finding on it for the rest of the queue — forty-four
   * pages changed by a click that meant "this one, please".
   *
   * So these are session-only, card-scoped, and invisible to the memory: the
   * send treats them the way it treats a card that is not going at all, as
   * decisions nobody made.
   */
  private readonly cardScopedUnticks = new Set<string>();

  /**
   * Ticks changed in this session but not yet written.
   *
   * The written state lives in the disposition records — see
   * `utils/review-check-memory` — and is only correct as of the last send. This
   * holds the difference, so a click reads back immediately rather than after a
   * round trip.
   */
  private readonly checkTicks = new Map<string, boolean>();

  /**
   * The page an untick was made on.
   *
   * Needed to tell "you just unticked this" from "this arrived unticked because
   * of something you did earlier" — the second wants explaining and the first
   * does not. The written record cannot answer it: a session override sits in
   * front of the record from the moment it is set, so without this the note
   * would never fire on the page after the one where the untick happened.
   */
  private readonly untickedOn = new Map<string, string>();

  /**
   * What the last send changed about the check memory, and what it was before.
   *
   * Undo has to put the memory back, not just the work items: a send that mutes
   * four checks and settles two changes what the next forty pages will show,
   * and an undo that left those in place would silently keep answering
   * questions on behalf of a decision the operator took back. One slot, because
   * the review's undo only ever reaches the most recent send.
   */
  private lastSendMemoryUndo: ReviewCheckWrite[] = [];

  /** Bumped on every change so templates re-read without manual change detection. */
  private readonly versionSubject = new BehaviorSubject(0);
  readonly version$ = this.versionSubject.asObservable();

  constructor(
    private readonly http: HttpClient,
    private readonly issueQueue: PlanV2IssueQueueService
  ) {}

  /**
   * Re-read the queue after a batch write.
   *
   * The single-issue PATCH keeps the queue service's local map in step through
   * its own `tap`; a batch goes straight to the endpoint and would leave that
   * map describing the world as it was before the send. What depends on it is
   * not cosmetic: the claimed-gap total is derived from it, the site-wide
   * suppression reads it to know what has been answered, and the cards read it
   * to know they are done. All three would have gone on showing the pre-send
   * state until something else happened to reload.
   *
   * One extra GET per send, on a keystroke that already made a round trip.
   */
  private withQueueRefresh<T>(domain: string, source: Observable<T>): Observable<T> {
    return source.pipe(
      switchMap((value) => this.issueQueue.load(this.scope(domain)).pipe(map(() => value)))
    );
  }

  /**
   * A card announcing itself, or announcing that its payload changed.
   *
   * Re-registration is an update rather than a duplicate: a technical payload
   * is rebuilt when the domain rollup lands, and the operator must not end up
   * sending the earlier version of it.
   */
  register(urlId: string, payload: PlanV2IssueCardPayload): void {
    const key = (payload?.issueKey || '').trim();
    const id = (urlId || '').trim();
    if (!key || !id) return;

    let forUrl = this.itemsByUrlId.get(id);
    if (!forUrl) {
      forUrl = new Map();
      this.itemsByUrlId.set(id, forUrl);
    }
    const existing = forUrl.get(key);
    if (existing && existing.payload === payload) return;
    forUrl.set(key, { issueKey: key, payload });
    this.versionSubject.next(this.versionSubject.value + 1);
  }

  /**
   * The technical checks on a page, announced by the card that renders them.
   *
   * Replaces the old site-wide-only channel. Registering everything rather than
   * only the template defects is what lets a page-scoped check be unticked and
   * stay unticked: the mute is a fact about the CHECK, and the crawler's scope
   * tag decides only whether SENDING it also settles it for good.
   */
  registerChecks(urlId: string, cardIssueKey: string, checks: readonly RegisteredCheck[]): void {
    const id = (urlId || '').trim();
    const card = (cardIssueKey || '').trim();
    if (!id || !card) return;

    const existing = this.checksByUrlId.get(id) ?? new Map<string, RegisteredCheck>();
    // A card re-registers when its data settles; its own rows are replaced
    // rather than merged, so a check the rollup reclassified does not linger
    // under its old scope.
    let changed = false;
    for (const [code, check] of [...existing]) {
      if (check.cardIssueKey === card && !checks.some((c) => c.code === code)) {
        existing.delete(code);
        changed = true;
      }
    }
    for (const check of checks) {
      const code = (check?.code || '').trim();
      if (!code) continue;
      const prior = existing.get(code);
      if (prior && prior.cardIssueKey === card && prior.siteWide === check.siteWide) continue;
      existing.set(code, { code, cardIssueKey: card, siteWide: check.siteWide });
      changed = true;
    }
    if (!changed) return;
    this.checksByUrlId.set(id, existing);
    // No version bump, for the same reason `registerCardItems` has none: this
    // now runs from `ngDoCheck`, and pushing a subject that templates read in
    // the same change-detection pass earns
    // ExpressionChangedAfterItHasBeenChecked. Nothing needs it — every reader
    // is a getter (`reviewSelected`, `sendLabel`) re-evaluated each pass. The
    // bumps that matter come from `toggleCheck` and `toggleItem`, which are
    // clicks.
  }

  /**
   * A card announcing the rows it renders.
   *
   * Replaced wholesale rather than merged: a card whose data settles late — the
   * supporting-pages columns arrive after the topic snapshot — must not leave a
   * row registered that it no longer shows, or the page would send a page the
   * operator never saw.
   */
  registerCardItems(urlId: string, cardIssueKey: string, itemKeys: readonly string[]): void {
    const id = (urlId || '').trim();
    const card = (cardIssueKey || '').trim();
    if (!id || !card) return;

    const forUrl = this.cardItemsByUrlId.get(id) ?? new Map<string, string[]>();
    const next = itemKeys.map((k) => (k || '').trim()).filter(Boolean);
    const prior = forUrl.get(card);
    if (prior && prior.length === next.length && prior.every((k, i) => k === next[i])) return;

    forUrl.set(card, next);
    this.cardItemsByUrlId.set(id, forUrl);
    // Deliberately no version bump. This runs from `ngDoCheck`, and bumping a
    // subject that templates read during the same change-detection pass is how
    // you earn ExpressionChangedAfterItHasBeenChecked. Nothing needs it either:
    // rows arrive ticked, which is what a card with no registration already
    // renders, so registering changes nothing on screen. `toggleItem` bumps,
    // because that is a real change and it comes from a click.
  }

  /** The key for one row. Card-scoped, and the card key already carries the URL. */
  itemKey(cardIssueKey: string, itemId: string): string {
    return `${(cardIssueKey || '').trim()}::${(itemId || '').trim()}`;
  }

  isItemTicked(itemKey: string): boolean {
    return !this.deselectedItems.has((itemKey || '').trim());
  }

  toggleItem(itemKey: string): void {
    const key = (itemKey || '').trim();
    if (!key) return;
    if (this.deselectedItems.has(key)) this.deselectedItems.delete(key);
    else this.deselectedItems.add(key);
    this.versionSubject.next(this.versionSubject.value + 1);
  }

  /** The rows a card will actually send. */
  tickedItemIds(urlId: string, cardIssueKey: string): string[] {
    const card = (cardIssueKey || '').trim();
    const keys = this.cardItemsByUrlId.get((urlId || '').trim())?.get(card) ?? [];
    return keys.filter((k) => this.isItemTicked(this.itemKey(card, k)));
  }

  /** Whether a card has announced its rows yet. */
  hasCardItems(urlId: string, cardIssueKey: string): boolean {
    const keys =
      this.cardItemsByUrlId.get((urlId || '').trim())?.get((cardIssueKey || '').trim()) ?? [];
    return keys.length > 0;
  }

  /** A card that listed rows and has none ticked left. Same rule as the checks. */
  private cardEmptiedByItemTicks(urlId: string, cardIssueKey: string): boolean {
    const card = (cardIssueKey || '').trim();
    const keys = this.cardItemsByUrlId.get((urlId || '').trim())?.get(card) ?? [];
    return keys.length > 0 && this.tickedItemIds(urlId, card).length === 0;
  }

  checks(urlId: string): RegisteredCheck[] {
    return [...(this.checksByUrlId.get((urlId || '').trim())?.values() ?? [])];
  }

  /**
   * Codes muted by a previous page, as written.
   *
   * Memoised on the records map's identity, which is safe because the queue
   * service replaces the map rather than mutating it. Without this, every
   * checkbox on the page re-scans every disposition on every change-detection
   * pass — and a technical card on a live page is a dozen checkboxes.
   */
  private mutedCache: { source: unknown; codes: Set<string> } | null = null;

  private mutedCodes(): Set<string> {
    const source = this.issueQueue.records;
    if (this.mutedCache?.source === source) return this.mutedCache.codes;
    const codes = mutedCheckCodes(source.values());
    this.mutedCache = { source, codes };
    return codes;
  }

  /**
   * Whether one check arrives ticked.
   *
   * Ticked is the default and muted is the exception, which is the same way
   * round as the card selection above and for the same reason: a check that
   * appears after the operator has looked at the page — the technical payload
   * is rebuilt when the domain rollup lands — must arrive ticked, not silently
   * skipped.
   */
  /** The tick as one card renders it: the check's own state, minus this card's. */
  isCheckTickedOnCard(cardIssueKey: string, code: string): boolean {
    if (this.cardScopedUnticks.has(this.itemKey(cardIssueKey, code))) return false;
    return this.isCheckTicked(code);
  }

  isCheckTicked(code: string): boolean {
    const c = (code || '').trim();
    if (!c) return true;
    const local = this.checkTicks.get(c);
    if (local !== undefined) return local;
    return !this.mutedCodes().has(c);
  }

  /**
   * Whether a check is unticked because of an earlier page rather than this one.
   *
   * Lets the card explain itself instead of presenting an unticked box with no
   * account of who emptied it. Two ways to be earlier: a mute written in a
   * previous session, or an untick made on a previous page of this walk.
   */
  checkMutedEarlier(code: string, urlId: string): boolean {
    const c = (code || '').trim();
    if (!c || this.isCheckTicked(c)) return false;
    const here = this.untickedOn.get(c);
    return here ? here !== (urlId || '').trim() : this.mutedCodes().has(c);
  }

  toggleCheck(code: string, urlId: string, cardIssueKey = ''): void {
    const c = (code || '').trim();
    if (!c) return;

    // Unticked only because another row on this card was soloed: the click is
    // "this one too", and it lifts that without touching the check's own state.
    const scoped = this.itemKey(cardIssueKey, c);
    if (cardIssueKey && this.cardScopedUnticks.has(scoped)) {
      this.cardScopedUnticks.delete(scoped);
      this.checkTicks.set(c, true);
      this.untickedOn.delete(c);
      this.versionSubject.next(this.versionSubject.value + 1);
      return;
    }

    const ticked = !this.isCheckTicked(c);
    this.checkTicks.set(c, ticked);
    if (ticked) this.untickedOn.delete(c);
    else this.untickedOn.set(c, (urlId || '').trim());
    this.versionSubject.next(this.versionSubject.value + 1);
  }

  /** Ticked checks on a card, for the payload that names what it will fix. */
  tickedCodesForCard(urlId: string, cardIssueKey: string): Set<string> {
    const card = (cardIssueKey || '').trim();
    const out = new Set<string>();
    for (const check of this.checks(urlId)) {
      if (check.cardIssueKey === card && this.isCheckTickedOnCard(card, check.code)) {
        out.add(check.code);
      }
    }
    return out;
  }

  /**
   * A card that registered checks and has none ticked left.
   *
   * Unticking the last check under a card is the same statement as unticking
   * the card, and leaving the card ticked would send a work item that names no
   * work.
   */
  private cardEmptiedByCheckTicks(urlId: string, cardIssueKey: string): boolean {
    const card = (cardIssueKey || '').trim();
    const own = this.checks(urlId).filter((c) => c.cardIssueKey === card);
    return own.length > 0 && !own.some((c) => this.isCheckTickedOnCard(card, c.code));
  }

  /** Either kind of row can empty a card, and an emptied card is unticked. */
  private cardEmptied(urlId: string, cardIssueKey: string): boolean {
    return (
      this.cardEmptiedByCheckTicks(urlId, cardIssueKey) ||
      this.cardEmptiedByItemTicks(urlId, cardIssueKey)
    );
  }

  items(urlId: string): ReviewSelectableItem[] {
    return [...(this.itemsByUrlId.get((urlId || '').trim())?.values() ?? [])];
  }

  isSelected(issueKey: string): boolean {
    if (this.deselected.has(issueKey)) return false;
    const urlId = this.urlIdForIssueKey(issueKey);
    return !urlId || !this.cardEmptied(urlId, issueKey);
  }

  private urlIdForIssueKey(issueKey: string): string {
    for (const [urlId, items] of this.itemsByUrlId) {
      if (items.has(issueKey)) return urlId;
    }
    return '';
  }

  /**
   * Tick a card and everything under it.
   *
   * The card tick means "send this card", and once its rows have emptied it the
   * only reading of a click on it is "send all of this after all" — nothing
   * else would change anything, because a card with no ticked rows is unticked
   * however its own box is set.
   *
   * That was also a real defect, not only an odd semantic. `[checked]` is bound
   * to `reviewSelected`, and Angular writes to the input only when the bound
   * value CHANGES. Clicking an emptied card could not change it — it was
   * already false and stayed false — so the browser kept the box the user had
   * just ticked while the label beside it still read "Not this one". A checkbox
   * disagreeing with its own label is worse than either state.
   *
   * Explicit `true` on each check rather than deleting the override, because a
   * check muted on an earlier page has a written mute behind it: dropping the
   * local override would fall straight back through to that and untick again.
   */
  selectWholeCard(urlId: string, cardIssueKey: string): void {
    const id = (urlId || '').trim();
    const card = (cardIssueKey || '').trim();
    if (!card) return;

    this.deselected.delete(card);
    for (const check of this.checks(id)) {
      if (check.cardIssueKey !== card) continue;
      this.checkTicks.set(check.code, true);
      this.untickedOn.delete(check.code);
      this.cardScopedUnticks.delete(this.itemKey(card, check.code));
    }
    for (const key of this.cardItemsByUrlId.get(id)?.get(card) ?? []) {
      this.deselectedItems.delete(this.itemKey(card, key));
    }
    this.versionSubject.next(this.versionSubject.value + 1);
  }

  /**
   * Turn a card back on because one of its rows was clicked.
   *
   * The rows stay visible and clickable while a card is off, and clicking one is
   * the operator saying "actually, this". So it clears the card's own
   * deselection and ticks the row they pointed at — nothing else.
   *
   * Deliberately NOT "only this row". Turning a card off never touched its
   * rows, so turning it back on restores what they had picked; unticking the
   * others here would invent a decision they did not make, and on a technical
   * card that decision writes a mute for every one of them for the rest of the
   * period. Reactivating and then unticking is two clicks; a silent mute across
   * forty-four pages is not recoverable by clicking.
   */
  reactivateCardWithItem(urlId: string, cardIssueKey: string, itemId: string): void {
    const card = (cardIssueKey || '').trim();
    const id = (urlId || '').trim();
    if (!card) return;

    this.deselected.delete(card);
    for (const key of this.cardItemsByUrlId.get(id)?.get(card) ?? []) {
      if (key === itemId) this.deselectedItems.delete(this.itemKey(card, key));
      else this.deselectedItems.add(this.itemKey(card, key));
    }
    this.versionSubject.next(this.versionSubject.value + 1);
  }

  /**
   * The same, for a technical check — and the reason the scoping above exists.
   *
   * The others are unticked ON THIS CARD, not muted. Clicking one row means
   * "this one, on this page"; it is not the operator declining the other seven
   * checks for the rest of the period, and writing that would be putting words
   * in their mouth across forty-four pages they have not seen.
   */
  reactivateCardWithCheck(urlId: string, cardIssueKey: string, code: string): void {
    const card = (cardIssueKey || '').trim();
    const c = (code || '').trim();
    if (!card || !c) return;

    this.deselected.delete(card);
    this.checkTicks.set(c, true);
    this.untickedOn.delete(c);
    this.cardScopedUnticks.delete(this.itemKey(card, c));

    for (const check of this.checks((urlId || '').trim())) {
      if (check.cardIssueKey !== card || check.code === c) continue;
      this.cardScopedUnticks.add(this.itemKey(card, check.code));
    }
    this.versionSubject.next(this.versionSubject.value + 1);
  }

  /** Whether a card is unticked only because its rows are. */
  cardEmptiedByRows(urlId: string, cardIssueKey: string): boolean {
    return this.cardEmptied((urlId || '').trim(), cardIssueKey);
  }

  toggle(issueKey: string): void {
    const key = (issueKey || '').trim();
    if (!key) return;
    if (this.deselected.has(key)) this.deselected.delete(key);
    else this.deselected.add(key);
    this.versionSubject.next(this.versionSubject.value + 1);
  }

  split(urlId: string): ReviewSelectionSplit {
    // A card whose every check has been unticked counts as unticked, so the
    // split sees the same thing the operator does.
    const effective = new Set(this.deselected);
    for (const item of this.items(urlId)) {
      if (this.cardEmptied(urlId, item.issueKey)) effective.add(item.issueKey);
    }
    return splitReviewSelection(this.items(urlId), effective);
  }

  /**
   * What this page's send writes about the checks themselves.
   *
   * The rules live in `utils/review-check-writes`, where they can be read and
   * tested without a component tree around them. This is the part that only the
   * service can do: read what has already been written, and hold on to the undo
   * until the operator either presses it or moves on.
   */
  private checkMemoryWrites(urlId: string): ReviewCheckWrite[] {
    const settled = new Set<string>();
    for (const record of this.issueQueue.records.values()) {
      if (record?.issueType !== 'site_wide_technical' || record.queue !== 'work_item') continue;
      const code = siteWideCodeFromKey(record.issueKey);
      if (code) settled.add(code);
    }

    // Checks on a card that is not being sent are not decisions. Putting the
    // Technical card aside says "not this page's technical work"; it does not
    // say the operator queued eight template fixes, and it does not say they
    // declined eight checks either. Without this the dismissed card still wrote
    // every ticked site-wide check as work_item — settling it for the whole
    // period on the back of a send that never included it.
    const sending = this.checks(urlId).filter((check) => {
      // Soloing another row on this card is not a decision about this one — the
      // operator clicked ONE row, they did not decline the rest.
      if (this.cardScopedUnticks.has(this.itemKey(check.cardIssueKey, check.code))) return false;

      // Turning the card off is where scope decides.
      //
      // A SITE-WIDE check is one template defect for the whole site, so
      // declining it is a statement about the site however it was declined —
      // by its own tick or by the card's. The route the operator took to say
      // "not this" cannot change what "this" is.
      //
      // A page-specific check is different: turning the card off says "not this
      // page's technical work", which says nothing about the next page's title
      // or word count, so it is left undecided.
      if (this.deselected.has(check.cardIssueKey)) return check.siteWide;

      return true;
    });

    const plan = reviewCheckWritePlan(
      sending.map((check) => ({
        code: check.code,
        siteWide: check.siteWide,
        // A card that is off sends nothing, so nothing on it counts as ticked —
        // including the site-wide rows above, which is what turns "card off"
        // into a mute for them rather than a settle.
        ticked:
          !this.deselected.has(check.cardIssueKey) && this.isCheckTickedOnCard(check.cardIssueKey, check.code)
      })),
      { urlId, url: this.urlForItems(urlId), muted: this.mutedCodes(), settled }
    );

    this.lastSendMemoryUndo = plan.undo;
    return plan.writes;
  }

  /**
   * One request, because it was one decision.
   *
   * As N separate writes this would be N round trips, N chances to
   * half-succeed, and nothing single for undo to take back.
   */
  send(domain: string, urlId: string): Observable<ReviewSelectableItem[]> {
    const split = this.split(urlId);
    const items = reviewSendRequest(split);

    // A send that queues NOTHING is a page put aside.
    //
    // Dismissals decide the card they name, not the page — that rule exists so
    // waving off one Technical card cannot retire a page whose content findings
    // were never read. But when the operator unticks EVERY card and sends, they
    // have read the page and queued nothing, and without a page-level record it
    // comes back tomorrow carrying "No actionable findings on these checks",
    // which is a page you cannot act on and cannot clear.
    //
    // `url_ignored` is that record, and it is the same one the Put aside button
    // writes — the operator did the same thing, one card at a time. It also
    // means the page appears under Show hidden in Inventory with a Restore,
    // which a page stranded in the belt never did.
    const pageAside: Array<{ issueKey: string; queue: 'dismissed'; payload: PlanV2IssueCardPayload }> =
      !split.send.length && split.putAside.length
        ? [
            {
              issueKey: buildPlanV2IssueKey(urlId.trim(), 'url_ignored'),
              queue: 'dismissed' as const,
              payload: {
                issueKey: buildPlanV2IssueKey(urlId.trim(), 'url_ignored'),
                urlId: urlId.trim(),
                url: split.putAside[0]?.payload?.url || '',
                issueType: 'url_ignored',
                cardTitle: 'URL ignored',
                changeSummary: 'Reviewed and queued nothing — restore from Inventory to see it again.',
                why: 'Every card on this page was set aside during review.',
                priorityScore: split.putAside[0]?.payload?.priorityScore ?? null
              }
            }
          ]
        : [];

    // What this page's ticks mean for the NEXT page: which checks are settled
    // for good, which arrive unticked, and which have just been un-muted. This
    // used to answer every site-wide code with the page's overall verdict,
    // which meant putting a page aside silently settled six template defects
    // the operator had not looked at.
    const memory = this.checkMemoryWrites(urlId);

    return this.withQueueRefresh(
      domain,
      this.http
        .post<{ success: boolean }>(`${this.API_URL}/urls/action-plan/v2/issues/batch`, {
          domain: this.scope(domain),
          items: [...items, ...memory, ...pageAside]
        })
        .pipe(map(() => [...split.send, ...split.putAside]))
    );
  }

  /** Put every key the send touched back in the queue it came from. */
  undo(domain: string, sent: readonly ReviewSelectableItem[]): Observable<void> {
    // The check memory the send wrote goes back to exactly what it was, not to
    // empty: a mute set two pages ago is not this send's to clear, and settling
    // a template defect on the back of a send that was taken back would keep
    // answering a question the operator un-answered.
    const memory = [...this.lastSendMemoryUndo];
    this.lastSendMemoryUndo = [];

    return this.withQueueRefresh(
      domain,
      this.http
        .post<{ success: boolean }>(`${this.API_URL}/urls/action-plan/v2/issues/batch`, {
          domain: this.scope(domain),
          items: [...reviewUndoRequest(sent), ...memory]
        })
        .pipe(map(() => undefined))
    );
  }

  /** Ticks everything again, so a page returned to by undo reads as untouched. */
  reselectAll(items: readonly ReviewSelectableItem[]): void {
    const urlId = String(items[0]?.payload?.urlId || '').trim();

    for (const item of items) this.deselected.delete(item.issueKey);

    // Every OTHER exception layer this page carried, cleared with them.
    //
    // This used to clear the card ticks alone, which was all there was when it
    // was written. Three layers have been added since — per-check ticks, the
    // page they were unticked on, per-row ticks, and card-scoped unticks — and
    // none of them were being taken back. The undo deleted the mutes from the
    // database and left the screen still saying "muted earlier" over checks
    // whose mute no longer existed: a claim about a decision that had just been
    // reversed, and one that would evaporate on the next reload.
    //
    // Scoped to THIS page's cards. `checkTicks` is keyed by code and shared
    // across the queue, so clearing it wholesale would take back unticks made
    // on pages the operator never undid.
    const cardKeys = new Set(items.map((item) => item.issueKey));
    for (const check of this.checks(urlId)) {
      if (!cardKeys.has(check.cardIssueKey)) continue;
      this.checkTicks.delete(check.code);
      this.untickedOn.delete(check.code);
      this.cardScopedUnticks.delete(this.itemKey(check.cardIssueKey, check.code));
    }
    for (const card of cardKeys) {
      for (const key of this.cardItemsByUrlId.get(urlId)?.get(card) ?? []) {
        this.deselectedItems.delete(this.itemKey(card, key));
      }
    }

    this.versionSubject.next(this.versionSubject.value + 1);
  }

  /** Any registered payload knows the page's URL; they all share it. */
  private urlForItems(urlId: string): string {
    return this.items(urlId)[0]?.payload?.url || '';
  }

  private scope(domain: string): string {
    return (domain || '').trim().toLowerCase().replace(/^www\./, '');
  }
}
